export const articles = [
  {
    id: 'art-001',
    slug: 'lead-generation-system',
    category: 'Marketing Strategy',
    categoryColor: 'text-teal',
    categoryBg: 'bg-teal/10',
    title: 'How to build a lead generation system that works while you sleep',
    excerpt:
      'Most SMBs rely on word of mouth and referrals. Here is how to build a repeatable acquisition system that fills your pipeline consistently — without burning out your team.',
    readTime: '6 min read',
    date: 'July 2026',
    gradientColors: 'rgba(0,184,148,0.2), rgba(0,206,201,0.05)',
    content: `
Most small businesses grow through referrals and word of mouth — and that's a great start. But referrals are unpredictable. You can't scale what you can't control.A lead generation system is a repeatable, measurable process that consistently brings new potential customers into your business. Here's how to build one.

## Step 1: Define your ideal customer profile

Before you can generate leads, you need to know exactly who you're trying to attract. Get specific:

- What industry are they in?
- What size is their business?
- What problem are they trying to solve?
- Where do they spend time online?
- What does a good outcome look like for them?

The more specific you are, the more effective every other part of your system will be.

## Step 2: Choose your acquisition channels

You don't need to be everywhere. Pick 2-3 channels where your ideal customers actually spend time:

- For B2B businesses: LinkedIn organic content, Google Search Ads, email outreach
- For B2C businesses: Instagram/TikTok, Google Shopping, Facebook Ads
- For local businesses: Google Business Profile, local SEO, community groups

Start with one channel, get it working, then add another.

## Step 3: Create a lead magnet

A lead magnet is something valuable you give away in exchange for contact details. Good lead magnets:

- Solve a specific, immediate problem
- Are quick to consume (checklist, template, short guide)
- Demonstrate your expertise
- Are directly related to your paid service

Examples: "5-point website audit checklist", "90-day social media content calendar template", "Pricing calculator for your service"

## Step 4: Build a simple nurture sequence

Most leads aren't ready to buy immediately. A nurture sequence keeps you top of mind until they are:

1. Welcome email (deliver the lead magnet)
2. Value email (share a useful tip or insight)
3. Case study or social proof email
4. Soft offer email
5. Direct offer email

Automate this with any email marketing tool. The goal is to move people from "interested" to "ready to buy" without manual effort.

## Step 5: Track and optimise

A system you can't measure is a system you can't improve. Track:

- Cost per lead (if using paid channels)
- Lead-to-customer conversion rate
- Which channels produce the highest quality leads
- Time from first contact to first purchase

Review these numbers monthly and make one change at a time.

## The compounding effect

The real power of a lead generation system is that it compounds. Each improvement you make — a better lead magnet, a more targeted ad audience, a stronger nurture sequence — builds on the last. After 6-12 months, you'll have a machine that generates leads predictably, even when you're not actively working on it.That's the goal: a business that grows while you sleep.
    `,
  },
  {
    id: 'art-002',
    slug: 'facebook-ads-conversion',
    category: 'Paid Advertising',
    categoryColor: 'text-primary',
    categoryBg: 'bg-primary/10',
    title: 'Why your Facebook Ads are not converting — and how to fix it in a week',
    excerpt:
      'The most common paid advertising mistakes we see SMBs make, and the specific changes that reliably improve conversion rates without increasing your budget.',
    readTime: '8 min read',
    date: 'June 2026',
    gradientColors: 'rgba(108,92,231,0.2), rgba(162,155,254,0.05)',
    content: `
Facebook Ads can be incredibly effective — or an expensive lesson in what not to do. After managing hundreds of campaigns for SMBs, we've identified the patterns that separate campaigns that convert from campaigns that drain budgets.

Here are the most common reasons your Facebook Ads aren't converting, and exactly how to fix them.

## Problem 1: You're targeting too broadly

The most common mistake. "Everyone" is not an audience. Broad targeting wastes budget on people who will never buy from you.

Fix: Build a custom audience from your existing customers, then create a lookalike audience based on that. If you don't have customer data yet, use detailed targeting with 3-5 specific interests that your ideal customer would have — not generic ones like "business" or "marketing."

## Problem 2: Your creative doesn't stop the scrollPeople scroll fast. If your ad looks like an ad, they'll skip it. If it looks like content, they'll stop.Fix: Test video over static images. Use real people, real situations, and real language — not stock photos and corporate copy. The first 3 seconds of a video determine whether someone watches the rest. Lead with the problem, not your solution.## Problem 3: Your offer isn't clear enough If someone has to think about what you're offering, they won't click. Clarity beats cleverness every time.

Fix: Your ad should answer three questions in 5 seconds: What is it? Who is it for? What do they do next? Remove anything that doesn't answer one of these questions.

## Problem 4: Your landing page doesn't match the ad

Ad-to-page mismatch is a conversion killer. If your ad promises one thing and your landing page delivers something different, people leave.

Fix: Your landing page headline should mirror your ad headline. The offer, tone, and visual style should be consistent. Remove navigation from landing pages — you want one action, not ten options.

## Problem 5: You're optimising for the wrong objective

If you're running a "Traffic" campaign but you want leads, you're paying for the wrong thing. Facebook will optimise for what you tell it to optimise for.

Fix: Use "Lead Generation" or "Conversions" objectives. Make sure your pixel is correctly installed and firing on the right events. Give the algorithm at least 50 conversion events per week to learn effectively.

## Problem 6: You're not testing enough

Running one ad and waiting to see if it works is not a strategy. Most ads don't work on the first try.

Fix: Always run at least 3-5 creative variations simultaneously. Test one variable at a time — headline, image, audience, or offer. Kill underperformers after 7 days and double down on winners.

## The one-week fix plan

- Day 1: Audit your pixel setup and conversion tracking
- Day 2: Rebuild your audience using customer data or tight interest targeting
- Day 3: Create 3 new ad creatives with clear, problem-led copy
- Day 4: Review and simplify your landing page
- Day 5: Launch new campaigns with correct objectives
- Day 6-7: Monitor early data, don't make changes yet After 7 days, you'll have real data to work with. Most clients see meaningful improvement within 2-3 weeks of implementing these changes.
    `,
  },
  {
    id: 'art-003',
    slug: '90-day-growth-sprint',
    category: 'Business Growth',
    categoryColor: 'text-amber',
    categoryBg: 'bg-amber/10',
    title: 'The 90-day growth sprint: A practical framework for small businesses',
    excerpt:
      'A structured approach to setting and executing on 90-day business goals — used by the most consistently growing businesses we work with.',
    readTime: '10 min read',
    date: 'May 2026',
    gradientColors: 'rgba(245,158,11,0.2), rgba(239,68,68,0.05)',
    content: `
Annual planning is broken for small businesses. Twelve months is too long to stay focused, too short to see major strategic shifts, and too easy to abandon when things get hard in March.

The 90-day sprint is different. It's long enough to make meaningful progress, short enough to stay focused, and structured enough to actually get things done.

## Why 90 days?

90 days is roughly one quarter. It's:

- Long enough to launch something, test it, and iterate
- Short enough that you can hold one clear priority in your head
- Aligned with how most businesses naturally review performance
- Short enough that if you pick the wrong priority, you haven't lost a year

## The 90-day sprint structure

### Week 1: The planning session

Don't skip this. The quality of your sprint depends entirely on the quality of your planning.Step 1: Review the last 90 days. What did you accomplish? What didn't happen? What did you learn? Be honest.

Step 2: Identify your one big constraint. What is the single biggest thing holding your business back right now? Not three things — one. This becomes your sprint focus.

Step 3: Set one primary goal. One goal. Measurable. Specific. Achievable in 90 days.

Step 4: Identify 3-5 key initiatives. What are the 3-5 specific projects that will move you toward your goal? Each initiative should have a clear owner, a deadline, and a definition of done.

### Weeks 2-12: Execution

Weekly rhythm:

- Monday: 30-minute team standup — what's the priority this week?
- Friday: 15-minute review — what got done, what's blocked?

Monthly rhythm:

- Mid-sprint review (week 6): Are you on track? Do you need to adjust?
- End-of-sprint review (week 12): What did you achieve? What did you learn?

## The most important rule: protect the sprint

The biggest threat to a 90-day sprint is distraction. New opportunities, urgent fires, and shiny objects will appear. Your job is to protect the sprint.

Before taking on anything new, ask: "Does this move us toward our 90-day goal?" If the answer is no, it goes on the backlog for the next sprint.

## Starting your first sprint

If you've never done this before, start simple:

1. Pick one goal
2. Identify three initiatives
3. Assign owners
4. Set a weekly check-in
5. Review at 45 days

The first sprint is always the hardest. By the third sprint, it becomes a natural rhythm — and you'll wonder how you ever ran your business without it.
    `,
  },
  {
    id: 'art-004',
    slug: 'brand-positioning-guide',
    category: 'Brand Positioning',
    categoryColor: 'text-indigo-light',
    categoryBg: 'bg-primary/10',
    title: 'How to position your brand so customers choose you over cheaper competitors',
    excerpt:
      "Competing on price is a race to the bottom. Here's how to position your brand so price becomes less relevant and customers actively seek you out.",
    readTime: '7 min read',
    date: 'April 2026',
    gradientColors: 'rgba(162,155,254,0.2), rgba(108,92,231,0.05)',
    content: `
If you're regularly losing deals to cheaper competitors, the problem usually isn't your price — it's your positioning.

Positioning is how you occupy a specific place in your customer's mind. Done well, it makes price comparisons feel irrelevant. Done poorly, you're just another option in a crowded market.## What positioning actually meansPositioning is not your tagline. It's not your logo. It's the answer to one question: "Why should I choose you over everyone else?"

Strong positioning is:

- Specific (not "we're the best")
- Relevant (solves a real problem your customer has)
- Differentiated (something competitors can't easily copy)
- Believable (backed by evidence)

## The positioning statement framework

A simple framework: For your target customer, your brand is the category that delivers the key benefit because of your reason to believe.

This isn't your marketing copy — it's your internal compass. Every marketing decision should be consistent with this statement.

## The four positioning strategies

Strategy 1: Niche positioning. Own a specific segment. "The marketing agency for independent restaurants" is more powerful than "the marketing agency for businesses."

Strategy 2: Methodology positioning. Own a unique approach. "The only agency that uses a specific process" differentiates you from everyone who does the same thing.

Strategy 3: Outcome positioning. Own a specific result. "We help e-commerce brands hit their first $1M in revenue" is more compelling than "we help businesses grow."Strategy 4: Values positioning. Own a belief. "We only work with businesses we genuinely believe in" attracts customers who share that value.

## How to find your positioning

The best positioning comes from your existing customers. Ask them:

- Why did you choose us over alternatives?
- What would you miss most if we disappeared?
- How would you describe us to a friend?

The language they use is your positioning. You're not inventing it — you're discovering it.

## Making it stick

Positioning only works if it's consistent. Every touchpoint — your website, your proposals, your social content, how your team answers the phone — should reinforce the same idea.

The businesses that win on positioning aren't necessarily the best at what they do. They're the most consistent at communicating what makes them different.
    `,
  },
  {
    id: 'art-005',
    slug: 'seo-for-small-business',
    category: 'Marketing Strategy',
    categoryColor: 'text-teal',
    categoryBg: 'bg-teal/10',
    title: 'SEO for small businesses: What actually moves the needle in 2026',
    excerpt:
      "Forget the outdated tactics. Here's what actually drives organic traffic for small businesses today — and what you should stop wasting time on.",
    readTime: '9 min read',
    date: 'March 2026',
    gradientColors: 'rgba(0,184,148,0.2), rgba(0,206,201,0.05)',
    content: `
SEO advice is everywhere, and most of it is either outdated, overly technical, or written for large enterprises with dedicated SEO teams. This guide is for small business owners who want to know what actually moves the needle.

## What's changed in 2026 Google's algorithm has gotten significantly better at understanding intent and quality. The tactics that worked in 2020 — keyword stuffing, low-quality link building, thin content — don't just fail to work now; they actively hurt you.

What works today is simpler to understand but harder to execute: be genuinely helpful to your target audience.

## The three things that matter most

### 1. Technical foundation

You don't need to be a developer to handle the basics:

- Page speed: Use Google PageSpeed Insights. Aim for a score above 70 on mobile.
- Mobile usability: Your site must work perfectly on a phone.
- Indexing: Make sure Google can actually find and read your pages.
- HTTPS: Non-negotiable in 2026.

Fix these once and they largely take care of themselves.

### 2. Content that answers real questions

The best SEO strategy for a small business is to become the most helpful resource in your niche. This means:

- Writing content that answers the questions your customers actually ask
- Going deeper than your competitors (not just longer — more useful)
- Updating content regularly so it stays accurate
- Using your own experience and expertise — not generic AI-generated content

One genuinely helpful article is worth more than ten thin ones.

### 3. Local SEO

If you serve customers in a specific location, local SEO is your highest-leverage activity:

- Claim and fully complete your Google Business Profile
- Get reviews from real customers (and respond to them)
- Ensure your name, address, and phone number are consistent everywhere online
- Create content that mentions your location naturally

## What to stop doing

- Buying links: Google is very good at identifying unnatural link patterns.
- Keyword stuffing: Write for humans. If it sounds unnatural, it is.
- Chasing every algorithm update: Focus on quality and consistency.
- Ignoring your existing content: Updating old content is often faster and more effective than creating new content.

## A realistic timeline

- Month 1-2: Technical fixes, Google Business Profile, keyword research
- Month 3-4: First content pieces published, on-page optimization
- Month 5-6: First ranking improvements visible
- Month 9-12: Meaningful traffic growth

SEO is a long game. But it's also the only marketing channel where the work you do today keeps paying off for years.
    `,
  },
  {
    id: 'art-006',
    slug: 'customer-retention-strategy',
    category: 'Business Growth',
    categoryColor: 'text-amber',
    categoryBg: 'bg-amber/10',
    title: "The retention strategy that's more profitable than any acquisition campaign",
    excerpt:
      "Acquiring a new customer costs 5-7x more than keeping an existing one. Here's how to build a retention system that turns one-time buyers into loyal advocates.",
    readTime: '8 min read',
    date: 'February 2026',
    gradientColors: 'rgba(245,158,11,0.2), rgba(239,68,68,0.05)',
    content: `
Every business focuses on acquisition. Ads, SEO, social media, referrals — all designed to bring in new customers. But the most profitable growth lever in most businesses isn't acquisition. It's retention.

A customer who buys twice is worth significantly more than two customers who each buy once. They cost less to serve, they're more likely to refer others, and they're more forgiving when things go wrong.

## Why retention is underinvested

Acquisition is visible. You can see the ad, the click, the conversion. Retention is invisible — it happens (or doesn't happen) quietly in the background.

Most businesses don't have a retention problem — they have a retention measurement problem. They don't know their churn rate, their repeat purchase rate, or their customer lifetime value. If you don't measure it, you can't improve it.

## The retention metrics you need to know

Repeat purchase rate: What percentage of customers buy more than once? Industry averages vary, but below 20% is a warning sign for most businesses.

Customer lifetime value (CLV): How much does a customer spend over their entire relationship with you?

Churn rate: What percentage of customers stop buying each month or year? Even a 5% reduction in churn can dramatically increase revenue.

## The five retention levers

### 1. Onboarding experience

The first 30 days after a purchase determine whether a customer becomes a loyal one. Make this experience exceptional:

- Send a personal welcome (not just an automated receipt)
- Proactively check in after delivery or service completion
- Provide resources that help them get maximum value
- Make it easy to ask questions

### 2. Proactive communication

Don't wait for customers to contact you. Reach out:

- Check in 30 days after purchase
- Share relevant content or tips
- Notify them of relevant new products or services
- Acknowledge milestones (anniversary of first purchase, etc.)

### 3. Loyalty and rewards

Not every business needs a formal loyalty program, but every business should reward repeat customers:

- Exclusive access to new products or services
- Priority service or faster turnaround
- Discounts for long-term customers
- Referral incentives

### 4. Feedback loops

Customers who feel heard are more likely to stay. Build feedback into your process:

- Post-purchase surveys (keep them short — 2-3 questions)
- Regular check-ins for service businesses
- Act on feedback and tell customers what you changed

### 5. Win-back campaigns

Some churn is inevitable. But many lapsed customers can be re-engaged:

- Identify customers who haven't purchased in 90+ days
- Send a personal re-engagement email
- Offer a reason to come back (new product, special offer, or just a genuine check-in)

## Starting this week

You don't need to implement all five levers at once. Start with one:

1. Calculate your repeat purchase rate
2. Identify your top 20% of customers by lifetime value
3. Send them a personal thank-you email this week

The businesses that grow most sustainably aren't the ones with the best acquisition campaigns. They're the ones that keep the customers they work so hard to acquire.
    `,
  },
];
